import { useCallback } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { useMutation } from '@apollo/client';
import { useModalRouter } from '@hooks/useModalRouter';
import { getRoute } from '@utils';
import { GetCheckoutAddressQueryResponse } from '@queries/checkout/queries/address';
import {
  AssignAddressOrderMutation,
  AssignAddressOrderMutationParams,
  AssignAddressOrderMutationResponse,
} from '@queries/address/mutations';
import { useOrderState } from '@hooks/useOrderState';
import { DeliveryType } from '@interfaces';
import {
  DeliveryOptionsContainer,
  AddressInput,
  AddressContainer,
  AddressTypographyDiv,
  AddressTypographyTitle,
  AddressTypographySubTitle,
  BorderedContainer,
  AddressLeftContainer,
  DeliveryPriceContainer,
  DeliveryPriceTypography,
  DeliveryPriceOldContainer,
} from '../styles';
import { SubtitleTypography } from '../../styles';
import {
  AddAddressDiv,
  AddAddressIconButton,
  AddAddressTypography,
} from './DeliveryOptions.styles';
import { Skeleton } from '@mui/material';
import { CurrencyFormatter } from '@utils/formatters/CurrencyFormatter';
import { useEventTracking } from '@hooks/useEventTracking';
import { InteractionEventTypes } from '@validators/Events/Interactions';

const currencyFormatter = new CurrencyFormatter({
  fallback: 'Gratis',
});
interface DeliveryContainerProps {
  addresses: GetCheckoutAddressQueryResponse['addresses'];
  currentOrder: GetCheckoutAddressQueryResponse['currentOrder'];
  deliveryCost: number;
  oldDeliveryCost: number;
  onSelectOption: (deliveryType: DeliveryType) => void;
  loading: boolean;
  missingInfo: boolean;
}

export function DeliveryOptions({
  addresses,
  currentOrder,
  deliveryCost,
  oldDeliveryCost,
  onSelectOption,
  loading,
  missingInfo,
}: DeliveryContainerProps) {
  // const { track, debugTrack } = useSegment();
  const { track } = useEventTracking();
  const modalRouter = useModalRouter();

  const { orderUuid } = useOrderState();

  const [assignAddress] = useMutation<
    AssignAddressOrderMutationResponse,
    AssignAddressOrderMutationParams
  >(AssignAddressOrderMutation());

  const handleSelectAddress = useCallback(
    async (addressUuid: string) => {
      if (!addresses?.length) return;
      await assignAddress({
        variables: { params: { orderUuid, addressUuid } },
        optimisticResponse: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          assignAddressOrder: {
            __typename: 'Order',
            id: currentOrder?.id,
            address: addresses.find((a) => a.uuid === addressUuid),
            minDeliveryDate: new Date().toISOString(),
            deliveryDate: currentOrder?.deliveryDate,
          },
        },
        refetchQueries: ['OrderDeliveryOptions'],
      });
      onSelectOption('DELIVERY');
    },
    [
      addresses,
      assignAddress,
      currentOrder?.deliveryDate,
      currentOrder?.id,
      onSelectOption,
      orderUuid,
    ],
  );

  const handleAddAddress = () => {
    track(InteractionEventTypes.ClickNewAddress, {
      address_quantity: addresses.length ?? 0,
    });
    void modalRouter.push(getRoute('checkoutAddAddresses'));
  };

  return (
    <DeliveryOptionsContainer>
      <SubtitleTypography>Despacho a domicilio</SubtitleTypography>
      {loading ? (
        <DeliveryOptionsSkeleton addresses={addresses} />
      ) : (
        <BorderedContainer missingInfo={missingInfo}>
          {addresses?.map((address) => (
            <AddressContainer key={address.id}>
              <AddressLeftContainer>
                <AddressInput
                  type="radio"
                  value={address.uuid}
                  key={address.id}
                  name="delivery-option"
                  checked={
                    currentOrder?.deliveryType === 'DELIVERY' &&
                    address.uuid === currentOrder?.address?.uuid
                  }
                  onChange={() => handleSelectAddress(address.uuid)}
                />
                <AddressTypographyDiv>
                  <AddressTypographyTitle>
                    {address.street} {address.streetNumber}
                    {address.apartment && ` (${address.apartment})`},{' '}
                    {address.commune}
                  </AddressTypographyTitle>
                  <AddressTypographySubTitle>
                    {address.name}
                  </AddressTypographySubTitle>
                </AddressTypographyDiv>
              </AddressLeftContainer>
              <DeliveryPriceContainer>
                {oldDeliveryCost && oldDeliveryCost > deliveryCost && (
                  <DeliveryPriceOldContainer>
                    {currencyFormatter.format(oldDeliveryCost)}
                  </DeliveryPriceOldContainer>
                )}
                <DeliveryPriceTypography>
                  {currencyFormatter.format(deliveryCost)}
                </DeliveryPriceTypography>
              </DeliveryPriceContainer>
            </AddressContainer>
          ))}
          <AddAddressDiv onClick={handleAddAddress}>
            <AddAddressIconButton>
              <AddIcon />
            </AddAddressIconButton>
            <AddAddressTypography>Agregar nueva dirección</AddAddressTypography>
          </AddAddressDiv>
        </BorderedContainer>
      )}
    </DeliveryOptionsContainer>
  );
}

interface DeliveryOptionsSkeletonProps {
  addresses: GetCheckoutAddressQueryResponse['addresses'];
}

const DeliveryOptionsSkeleton = ({
  addresses,
}: DeliveryOptionsSkeletonProps) => (
  <BorderedContainer>
    {addresses?.map((address) => (
      <AddressContainer key={address.id}>
        <AddressLeftContainer>
          <Skeleton variant="circular" width={24} height={24} />
          <AddressTypographyDiv>
            <Skeleton variant="rounded" width={200} height={20} />
            <Skeleton variant="rounded" width={100} height={19} />
          </AddressTypographyDiv>
        </AddressLeftContainer>
        <DeliveryPriceContainer>
          <Skeleton variant="rounded" width={50} height={20} />
        </DeliveryPriceContainer>
      </AddressContainer>
    ))}
    <AddAddressDiv>
      <AddAddressIconButton>
        <Skeleton variant="circular" width={20} height={20} />
      </AddAddressIconButton>
      <Skeleton variant="rounded" width={200} height={20} />
    </AddAddressDiv>
  </BorderedContainer>
);
